import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from "@mui/material";
import {
  School as SchoolIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CheckCircle as BilledIcon,
  PlayArrow as DemoIcon,
  Cancel as NotBilledIcon,
  Warning as ExpiredIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const Schools = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  // Mock data - Replace with API calls
  const schools = [
    {
      id: 1,
      name: "Greenwood High School",
      contactPerson: "John Doe",
      email: "john@greenwood.edu",
      phone: "+91 98765 43210",
      location: "Mumbai, Maharashtra",
      status: "Billed",
      revenue: 45000,
      joinDate: "2024-01-15",
      renewalDate: "2024-07-15",
      students: 1250,
    },
    {
      id: 2,
      name: "Sunshine Academy",
      contactPerson: "Jane Smith",
      email: "jane@sunshine.edu",
      phone: "+91 98765 43211",
      location: "Delhi, NCR",
      status: "Demo",
      revenue: 0,
      joinDate: "2024-06-20",
      renewalDate: null,
      students: 0,
    },
    {
      id: 3,
      name: "Riverside School",
      contactPerson: "Mike Johnson",
      email: "mike@riverside.edu",
      phone: "+91 98765 43212",
      location: "Bangalore, Karnataka",
      status: "Billed",
      revenue: 38000,
      joinDate: "2023-11-10",
      renewalDate: "2024-11-10",
      students: 980,
    },
    {
      id: 4,
      name: "Mountain View School",
      contactPerson: "Sarah Williams",
      email: "sarah@mountainview.edu",
      phone: "+91 98765 43213",
      location: "Pune, Maharashtra",
      status: "Not Billed",
      revenue: 0,
      joinDate: "2024-05-22",
      renewalDate: null,
      students: 0,
    },
    {
      id: 5,
      name: "City Central School",
      contactPerson: "David Brown",
      email: "david@citycentral.edu",
      phone: "+91 98765 43214",
      location: "Chennai, Tamil Nadu",
      status: "Billed",
      revenue: 42000,
      joinDate: "2023-08-05",
      renewalDate: "2024-08-05",
      students: 1100,
    },
    {
      id: 6,
      name: "Oakwood Academy",
      contactPerson: "Emily Davis",
      email: "emily@oakwood.edu",
      phone: "+91 98765 43215",
      location: "Hyderabad, Telangana",
      status: "Expired",
      revenue: 0,
      joinDate: "2022-03-15",
      renewalDate: "2024-03-15",
      students: 0,
    },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Billed":
        return "success";
      case "Demo":
        return "info";
      case "Not Billed":
        return "warning";
      case "Expired":
        return "error";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Billed":
        return <BilledIcon />;
      case "Demo":
        return <DemoIcon />;
      case "Not Billed":
        return <NotBilledIcon />;
      case "Expired":
        return <ExpiredIcon />;
      default:
        return <SchoolIcon />;
    }
  };

  const filteredSchools = schools.filter((school) => {
    const matchesSearch =
      school.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      school.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || school.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    handleFilterClose();
  };

  return (
    <Box>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Schools Management
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage all your school clients
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add New School
          </Button>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder="Search schools..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant={statusFilter !== "all" ? "contained" : "outlined"}
                  startIcon={<FilterIcon />}
                  onClick={handleFilterClick}
                >
                  {statusFilter === "all" ? "All Status" : statusFilter}
                </Button>
                <Menu
                  anchorEl={filterAnchor}
                  open={Boolean(filterAnchor)}
                  onClose={handleFilterClose}
                >
                  <MenuItem onClick={() => handleStatusFilter("all")}>All Status</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Billed")}>Billed</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Demo")}>Demo</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Not Billed")}>Not Billed</MenuItem>
                  <MenuItem onClick={() => handleStatusFilter("Expired")}>Expired</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Schools Table */}
        <Card>
          <CardContent>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>School Name</TableCell>
                    <TableCell>Contact Person</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Revenue</TableCell>
                    <TableCell>Students</TableCell>
                    <TableCell>Renewal Date</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSchools.map((school) => (
                    <TableRow
                      key={school.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => navigate(`/schools/${school.id}`)}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
                            <SchoolIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {school.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{school.contactPerson}</Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "flex", alignItems: "center", gap: 0.5 }}>
                            <EmailIcon fontSize="inherit" />
                            {school.email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LocationIcon fontSize="small" />
                          {school.location}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(school.status)}
                          label={school.status}
                          color={getStatusColor(school.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {school.revenue > 0 ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: "success.main" }}>
                            ₹{school.revenue.toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{school.students.toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        {school.renewalDate ? (
                          <Typography variant="body2">
                            {new Date(school.renewalDate).toLocaleDateString()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton size="small">
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add School Dialog */}
        <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Add New School</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="School Name" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Person" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" type="email" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone" required />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address" multiline rows={2} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="City" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="State" />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => setAddDialogOpen(false)}>
              Add School
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>
    </Box>
  );
};

export default Schools;

